import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowClockwise,
  CaretRight,
  CreditCard,
  Plug,
  PlugsConnected,
  Plus,
  Warning,
} from '@phosphor-icons/react';
import {
  disconnectStripeAccount,
  getBillingSettings,
  getStripeAccount,
  refreshStripeAccount,
  startStripeOnboarding,
  updateBillingSettings,
  listInvoiceTypes,
  type BillingSettings,
  type BillingSettingsUpdate,
  type FamilyDiscountModel,
  type StripeAccountStatus,
} from '@/api/billing';
import { listMyOrganizations } from '@/api/orgs';
import { LogoUploadField } from '@/components/ui/logo-upload-field';
import { FormSkeleton } from '@/components/ui/form-skeleton';
import { AccessDenied } from '@/components/AccessDenied';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/Toast';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useConfirm } from '@/components/ConfirmDialog';
import { InvoiceTypesPanel } from './InvoiceTypesPanel';

// Billing settings + Stripe Connect Express onboarding. Owner / administrator
// only — non-admins are bounced by the backend and the SettingsPanel hides
// the tile entirely. See plan + locked decisions D-1 … D-17.

const SUPPORTED_CURRENCIES = ['SEK', 'NOK', 'DKK', 'EUR'] as const;
const SUPPORTED_COUNTRIES = [
  'SE', 'NO', 'DK', 'FI', 'DE', 'FR', 'NL', 'ES', 'IT', 'BE', 'AT', 'PT', 'IE',
] as const;
const FISCAL_MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => i + 1);

// Stripe emits `currently_due` codes like `company.verification.document`,
// `person_1Te9Gm…XLcn.dob.day`, and `individual.address.city`. None of those
// read as Swedish to a human — strip the personal-token prefix, collapse the
// per-component dob/tos rows into one row, then look up the friendly label.
function normalizeStripeRequirementKey(code: string): string {
  let key = code
    .replace(/^person_[A-Za-z0-9]+\./, '')
    .replace(/^(individual|representative)\./, '');
  if (key.startsWith('dob.')) key = 'dob';
  if (key.startsWith('tos_acceptance.')) key = 'tos_acceptance';
  return key;
}

function dedupedStripeRequirements(codes: string[]): { key: string; raw: string }[] {
  const seen = new Set<string>();
  const out: { key: string; raw: string }[] = [];
  for (const raw of codes) {
    const key = normalizeStripeRequirementKey(raw);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ key, raw });
  }
  return out;
}

export function Billing() {
  const { slug = '' } = useParams();
  const { t } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();
  const stripeReturn = searchParams.get('stripe');

  const orgsQuery = useQuery({
    queryKey: ['my-orgs'],
    queryFn: () => listMyOrganizations(token),
    enabled: !!token,
  });
  const org = orgsQuery.data?.find((o) => o.slug === slug);
  const callerRole = org?.role ?? '';
  const canManage = callerRole === 'owner' || callerRole === 'administrator';

  const settingsQuery = useQuery({
    queryKey: ['billing-settings', slug],
    queryFn: () => getBillingSettings(token, slug),
    enabled: !!token && !!slug && canManage,
  });
  const accountQuery = useQuery({
    queryKey: ['billing-stripe-account', slug],
    queryFn: () => getStripeAccount(token, slug),
    enabled: !!token && !!slug && canManage,
  });

  // When Stripe redirects back with ?stripe=return we automatically refresh
  // the mirrored state so the page reflects the latest details_submitted /
  // charges_enabled flags before the org-admin clicks anywhere.
  useEffect(() => {
    if (stripeReturn === 'return' && token && slug && canManage) {
      void refreshStripeAccount(token, slug).then((next) => {
        queryClient.setQueryData(['billing-stripe-account', slug], next);
        searchParams.delete('stripe');
        setSearchParams(searchParams, { replace: true });
      });
    }
  }, [stripeReturn, token, slug, canManage, queryClient, searchParams, setSearchParams]);

  if (!canManage) {
    return <AccessDenied />;
  }

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 p-4 md:p-8">
      <header className="flex flex-col gap-1">
        <h1 className="font-heading text-2xl font-extrabold tracking-tight">{t('billing.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('billing.subtitle')}</p>
      </header>
      <Tabs
        defaultValue={searchParams.get('tab') === 'stripe' ? 'stripe' : 'settings'}
        className="flex flex-col gap-5"
      >
        <TabsList>
          <TabsTrigger value="settings">{t('billing.tab.settings')}</TabsTrigger>
          <TabsTrigger value="invoiceTypes">{t('billing.tab.invoiceTypes')}</TabsTrigger>
          <TabsTrigger value="stripe">{t('billing.tab.stripe')}</TabsTrigger>
        </TabsList>
        <TabsContent value="settings">
          {settingsQuery.data ? (
            <SettingsForm
              slug={slug}
              token={token}
              initial={settingsQuery.data}
              onSaved={(next) => queryClient.setQueryData(['billing-settings', slug], next)}
            />
          ) : (
            <FormSkeleton rows={4} />
          )}
        </TabsContent>
        <TabsContent value="invoiceTypes">
          <InvoiceTypesPanel slug={slug} token={token} confirm={confirm} />
        </TabsContent>
        <TabsContent value="stripe">
          <StripePanel
            slug={slug}
            token={token}
            account={accountQuery.data}
            isLoading={accountQuery.isPending}
            onChanged={() =>
              queryClient.invalidateQueries({ queryKey: ['billing-stripe-account', slug] })
            }
            confirm={confirm}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

interface SettingsFormProps {
  slug: string;
  token: string | null;
  initial: BillingSettings;
  onSaved: (next: BillingSettings) => void;
}

function SettingsForm({ slug, token, initial, onSaved }: SettingsFormProps) {
  const { t } = useTranslation('common');
  const toast = useToast();
  const [form, setForm] = useState<BillingSettingsUpdate>(() => ({
    fiscal_year_start_month: initial.fiscal_year_start_month,
    vat_default_rate_bp: initial.vat_default_rate_bp,
    default_due_days: initial.default_due_days,
    invoice_email_from: initial.invoice_email_from,
    invoice_email_reply_to: initial.invoice_email_reply_to,
    invoice_footer_text: initial.invoice_footer_text,
    reminder_offset_days_first: initial.reminder_offset_days_first,
    reminder_offset_days_second: initial.reminder_offset_days_second,
    reminder_offset_days_overdue: initial.reminder_offset_days_overdue,
    reminder_send_email: initial.reminder_send_email,
    reminder_send_push: initial.reminder_send_push,
    sender_address_line1: initial.sender_address_line1,
    sender_address_line2: initial.sender_address_line2,
    sender_postal_code: initial.sender_postal_code,
    sender_city: initial.sender_city,
    sender_country: initial.sender_country,
    organization_number: initial.organization_number,
    vat_number: initial.vat_number,
    bankgiro: initial.bankgiro,
    plusgiro: initial.plusgiro,
    iban: initial.iban,
    bic: initial.bic,
    f_skatt: initial.f_skatt,
    logo_object_key: initial.logo_object_key,
    family_billing_enabled: initial.family_billing_enabled,
    family_discount_model: initial.family_discount_model,
    family_flat_fee_minor: initial.family_flat_fee_minor,
    family_percentage_bp: initial.family_percentage_bp,
    family_tier_discounts_bp: initial.family_tier_discounts_bp,
    family_cap_minor: initial.family_cap_minor,
    family_tier_order: initial.family_tier_order,
    family_discount_invoice_type_id: initial.family_discount_invoice_type_id,
  }));

  const saveMutation = useMutation({
    mutationFn: () => updateBillingSettings(token, slug, form),
    onSuccess: (next) => {
      onSaved(next);
      toast.success(t('billing.settings.saved'));
    },
  });

  const vatPercent = useMemo(() => (form.vat_default_rate_bp / 100).toFixed(2), [form.vat_default_rate_bp]);

  return (
    <form
      className="flex flex-col gap-5 rounded-3xl border border-border/60 bg-card p-6"
      onSubmit={(e) => {
        e.preventDefault();
        saveMutation.mutate();
      }}
    >
      <div className="flex flex-col gap-1">
        <h2 className="text-lg font-semibold">{t('billing.settings.title')}</h2>
        <p className="text-sm text-muted-foreground">{t('billing.settings.description')}</p>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Field label={t('billing.settings.currency')}>
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <Badge variant={initial.currency_locked ? 'default' : 'secondary'}>
                {initial.currency || '—'}
              </Badge>
              <span className="text-xs text-muted-foreground">
                {initial.currency_locked
                  ? t('billing.settings.currencyLockedHint')
                  : t('billing.settings.currencyUnlockedHint')}
              </span>
            </div>
            {!initial.currency_locked && (
              <span className="text-xs text-muted-foreground">
                {t('billing.settings.currencySupported', {
                  list: SUPPORTED_CURRENCIES.join(' · '),
                })}
              </span>
            )}
          </div>
        </Field>

        <Field label={t('billing.settings.fiscalYear')}>
          <Select
            value={String(form.fiscal_year_start_month)}
            onValueChange={(v) =>
              setForm({ ...form, fiscal_year_start_month: Number(v) })
            }
          >
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FISCAL_MONTH_OPTIONS.map((m) => (
                <SelectItem key={m} value={String(m)}>
                  {m}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field label={t('billing.settings.vatDefault')}>
          <div className="flex flex-col gap-1">
            <Input
              type="number"
              min={0}
              max={10000}
              step={50}
              value={form.vat_default_rate_bp}
              onChange={(e) =>
                setForm({ ...form, vat_default_rate_bp: Number(e.target.value) })
              }
            />
            <span className="text-xs text-muted-foreground">
              {vatPercent}% {' · '} {t('billing.settings.vatHint')}
            </span>
          </div>
        </Field>

        <Field label={t('billing.settings.dueDays')}>
          <Input
            type="number"
            min={0}
            max={365}
            value={form.default_due_days}
            onChange={(e) => setForm({ ...form, default_due_days: Number(e.target.value) })}
          />
        </Field>

        <Field label={t('billing.settings.emailFrom')}>
          <Input
            type="email"
            value={form.invoice_email_from}
            onChange={(e) => setForm({ ...form, invoice_email_from: e.target.value })}
          />
        </Field>

        <Field label={t('billing.settings.emailReplyTo')}>
          <Input
            type="email"
            value={form.invoice_email_reply_to}
            onChange={(e) => setForm({ ...form, invoice_email_reply_to: e.target.value })}
          />
        </Field>
      </div>

      <fieldset className="flex flex-col gap-3 rounded-2xl border border-border/60 p-4">
        <legend className="px-2 text-sm font-semibold">{t('billing.settings.invoiceHeader')}</legend>
        <p className="text-xs text-muted-foreground">{t('billing.settings.invoiceHeaderHint')}</p>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Field label={t('billing.settings.senderAddressLine1')}>
            <Input
              value={form.sender_address_line1}
              onChange={(e) => setForm({ ...form, sender_address_line1: e.target.value })}
            />
          </Field>
          <Field label={t('billing.settings.senderAddressLine2')}>
            <Input
              value={form.sender_address_line2}
              onChange={(e) => setForm({ ...form, sender_address_line2: e.target.value })}
            />
          </Field>
          <Field label={t('billing.settings.senderPostalCode')}>
            <Input
              value={form.sender_postal_code}
              onChange={(e) => setForm({ ...form, sender_postal_code: e.target.value })}
            />
          </Field>
          <Field label={t('billing.settings.senderCity')}>
            <Input
              value={form.sender_city}
              onChange={(e) => setForm({ ...form, sender_city: e.target.value })}
            />
          </Field>
          <Field label={t('billing.settings.senderCountry')}>
            <Input
              maxLength={2}
              value={form.sender_country}
              onChange={(e) => setForm({ ...form, sender_country: e.target.value.toUpperCase() })}
              placeholder="SE"
            />
          </Field>
          <Field label={t('billing.settings.organizationNumber')}>
            <Input
              value={form.organization_number}
              onChange={(e) => setForm({ ...form, organization_number: e.target.value })}
              placeholder="802000-1234"
            />
          </Field>
          <Field label={t('billing.settings.vatNumber')}>
            <Input
              value={form.vat_number}
              onChange={(e) => setForm({ ...form, vat_number: e.target.value.toUpperCase() })}
              placeholder="SE802000123401"
            />
          </Field>
        </div>
        <LogoUploadField
          slug={slug}
          token={token}
          value={form.logo_object_key}
          label={t('billing.settings.invoiceLogo')}
          hint={t('billing.settings.invoiceLogoHint')}
          onChange={(key) => setForm({ ...form, logo_object_key: key })}
        />
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.f_skatt}
            onChange={(e) => setForm({ ...form, f_skatt: e.target.checked })}
          />
          {t('billing.settings.fSkatt')}
        </label>
      </fieldset>

      <fieldset className="flex flex-col gap-3 rounded-2xl border border-border/60 p-4">
        <legend className="px-2 text-sm font-semibold">{t('billing.settings.paymentInfo')}</legend>
        <p className="text-xs text-muted-foreground">{t('billing.settings.paymentInfoHint')}</p>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Field label={t('billing.settings.bankgiro')}>
            <Input
              value={form.bankgiro}
              onChange={(e) => setForm({ ...form, bankgiro: e.target.value })}
              placeholder="123-4567"
            />
          </Field>
          <Field label={t('billing.settings.plusgiro')}>
            <Input
              value={form.plusgiro}
              onChange={(e) => setForm({ ...form, plusgiro: e.target.value })}
              placeholder="12 34 56-7"
            />
          </Field>
          <Field label={t('billing.settings.iban')}>
            <Input
              value={form.iban}
              onChange={(e) => setForm({ ...form, iban: e.target.value.toUpperCase() })}
              placeholder="SE45 5000 0000 0583 9825 7466"
            />
          </Field>
          <Field label={t('billing.settings.bic')}>
            <Input
              value={form.bic}
              onChange={(e) => setForm({ ...form, bic: e.target.value.toUpperCase() })}
              placeholder="SBINSESS"
            />
          </Field>
        </div>
      </fieldset>

      <Field label={t('billing.settings.footer')}>
        <textarea
          className="min-h-[120px] rounded-2xl border border-border bg-background px-3 py-2 text-sm"
          value={form.invoice_footer_text}
          onChange={(e) => setForm({ ...form, invoice_footer_text: e.target.value })}
          placeholder={t('billing.settings.footerHint') ?? ''}
        />
      </Field>

      <fieldset className="flex flex-col gap-3 rounded-2xl border border-border/60 p-4">
        <legend className="px-2 text-sm font-semibold">{t('billing.settings.reminders')}</legend>
        <p className="text-xs text-muted-foreground">{t('billing.settings.remindersHint')}</p>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <ReminderField
            label={t('billing.settings.reminderFirst')}
            value={form.reminder_offset_days_first}
            onChange={(v) => setForm({ ...form, reminder_offset_days_first: v })}
          />
          <ReminderField
            label={t('billing.settings.reminderSecond')}
            value={form.reminder_offset_days_second}
            onChange={(v) => setForm({ ...form, reminder_offset_days_second: v })}
          />
          <ReminderField
            label={t('billing.settings.reminderOverdue')}
            value={form.reminder_offset_days_overdue}
            onChange={(v) => setForm({ ...form, reminder_offset_days_overdue: v })}
          />
        </div>
        <div className="flex flex-wrap gap-4 pt-2 text-sm">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.reminder_send_email}
              onChange={(e) => setForm({ ...form, reminder_send_email: e.target.checked })}
            />
            {t('billing.settings.reminderEmail')}
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.reminder_send_push}
              onChange={(e) => setForm({ ...form, reminder_send_push: e.target.checked })}
            />
            {t('billing.settings.reminderPush')}
          </label>
        </div>
      </fieldset>

      <FamilyBillingSection slug={slug} token={token} form={form} setForm={setForm} currency={initial.currency} />

      <div className="flex items-center justify-end gap-3">
        {saveMutation.isError && (
          <span className="text-sm text-red-600">{t('billing.settings.saveError')}</span>
        )}
        <Button type="submit" disabled={saveMutation.isPending}>
          {saveMutation.isPending ? t('common.submitting') : t('billing.settings.save')}
        </Button>
      </div>
    </form>
  );
}

function ReminderField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number | null;
  onChange: (v: number | null) => void;
}) {
  return (
    <Field label={label}>
      <Input
        type="number"
        min={0}
        max={365}
        value={value ?? ''}
        onChange={(e) => {
          const raw = e.target.value;
          onChange(raw === '' ? null : Number(raw));
        }}
      />
    </Field>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="font-medium">{label}</span>
      {children}
    </label>
  );
}

// MoneyInput edits an amount stored in minor units (öre/cents) but keeps the raw
// text the user is typing in local state so we never reformat mid-keystroke — the
// old approach re-derived value={(minor/100).toFixed(2)} on every change, which
// snapped "9" back to "9.00" and made the field impossible to type into. We only
// normalize to two decimals on blur. The currency code renders as a suffix.
function MoneyInput({
  valueMinor,
  onChangeMinor,
  currency,
  placeholder,
  className = 'w-40',
}: {
  valueMinor: number | null;
  onChangeMinor: (minor: number | null) => void;
  currency: string;
  placeholder?: string;
  className?: string;
}) {
  const toText = (minor: number | null) => (minor == null ? '' : (minor / 100).toFixed(2));
  const [text, setText] = useState(() => toText(valueMinor));

  // Re-sync from the stored value when it changes externally (e.g. form reset),
  // but not while the user's own typing already represents the same amount.
  useEffect(() => {
    const typedMinor =
      text.trim() === '' ? null : Math.round(Number.parseFloat(text) * 100);
    const typedIsFinite = text.trim() === '' || Number.isFinite(typedMinor);
    if (typedMinor !== valueMinor || !typedIsFinite) setText(toText(valueMinor));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueMinor]);

  return (
    <div className={`relative ${className}`}>
      <Input
        type="number"
        min={0}
        step="0.01"
        inputMode="decimal"
        placeholder={placeholder}
        className={currency ? 'pr-14' : undefined}
        value={text}
        onChange={(e) => {
          const raw = e.target.value;
          setText(raw);
          const v = raw.trim();
          if (v === '') {
            onChangeMinor(null);
            return;
          }
          const n = Number.parseFloat(v);
          onChangeMinor(Number.isFinite(n) ? Math.round(n * 100) : null);
        }}
        onBlur={() => {
          const n = Number.parseFloat(text);
          if (text.trim() !== '' && Number.isFinite(n)) setText(n.toFixed(2));
        }}
      />
      {currency && (
        <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-muted-foreground">
          {currency}
        </span>
      )}
    </div>
  );
}

// FamilyBillingSection edits the D4 family-discount config: enable toggle, model
// picker, conditional editors (tier list / flat fee / percentage), an optional
// cap, the tier ordering, and which invoice type the discount applies to.
function FamilyBillingSection({
  slug,
  token,
  form,
  setForm,
  currency,
}: {
  slug: string;
  token: string | null;
  form: BillingSettingsUpdate;
  setForm: (f: BillingSettingsUpdate) => void;
  currency: string;
}) {
  const { t } = useTranslation('common');
  const typesQuery = useQuery({
    queryKey: ['billing-invoice-types', slug, false],
    queryFn: () => listInvoiceTypes(token, slug, false),
    enabled: !!token && !!slug,
  });

  const model = form.family_discount_model;
  const tiers = form.family_tier_discounts_bp ?? [];
  const setTiers = (next: number[]) => setForm({ ...form, family_tier_discounts_bp: next });

  return (
    <fieldset className="flex flex-col gap-4 rounded-2xl border border-border/60 p-4">
      <legend className="px-2 text-sm font-semibold">{t('billing.family.title')}</legend>
      <p className="text-xs text-muted-foreground">{t('billing.family.hint')}</p>

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={form.family_billing_enabled}
          onChange={(e) => setForm({ ...form, family_billing_enabled: e.target.checked })}
        />
        {t('billing.family.enable')}
      </label>

      {form.family_billing_enabled && (
        <div className="flex flex-col gap-4">
          <Field label={t('billing.family.model')}>
            <Select
              value={model}
              onValueChange={(v) => setForm({ ...form, family_discount_model: v as FamilyDiscountModel })}
            >
              <SelectTrigger className="w-64">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(['none', 'sibling_tiered', 'flat_fee', 'percentage'] as const).map((m) => (
                  <SelectItem key={m} value={m}>
                    {t(`billing.family.models.${m}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          {model === 'sibling_tiered' && (
            <Field label={t('billing.family.tiers')}>
              <div className="flex flex-col gap-2">
                <p className="text-xs text-muted-foreground">{t('billing.family.tiersHint')}</p>
                {tiers.map((bp, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="w-28 text-sm text-muted-foreground">
                      {t('billing.family.tierPosition', { n: i + 1 })}
                    </span>
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      className="w-24"
                      value={String(bp / 100)}
                      onChange={(e) => {
                        const pct = Number.parseFloat(e.target.value);
                        const next = [...tiers];
                        next[i] = Number.isFinite(pct) ? Math.round(pct * 100) : 0;
                        setTiers(next);
                      }}
                    />
                    <span className="text-sm text-muted-foreground">%</span>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setTiers(tiers.filter((_, j) => j !== i))}
                    >
                      {t('common.remove')}
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="self-start"
                  onClick={() => setTiers([...tiers, 0])}
                >
                  <Plus weight="bold" />
                  {t('billing.family.addTier')}
                </Button>
              </div>
            </Field>
          )}

          {model === 'flat_fee' && (
            <Field label={t('billing.family.flatFee')}>
              <MoneyInput
                currency={currency}
                valueMinor={form.family_flat_fee_minor}
                onChangeMinor={(minor) => setForm({ ...form, family_flat_fee_minor: minor })}
              />
            </Field>
          )}

          {model === 'percentage' && (
            <Field label={t('billing.family.percentage')}>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min={0}
                  max={100}
                  step="0.01"
                  className="w-28"
                  value={form.family_percentage_bp == null ? '' : String(form.family_percentage_bp / 100)}
                  onChange={(e) => {
                    const pct = Number.parseFloat(e.target.value);
                    setForm({
                      ...form,
                      family_percentage_bp: Number.isFinite(pct) ? Math.round(pct * 100) : null,
                    });
                  }}
                />
                <span className="text-sm text-muted-foreground">%</span>
              </div>
            </Field>
          )}

          <Field label={t('billing.family.cap')}>
            <MoneyInput
              currency={currency}
              valueMinor={form.family_cap_minor}
              placeholder={t('billing.family.capPlaceholder')}
              onChangeMinor={(minor) => setForm({ ...form, family_cap_minor: minor })}
            />
          </Field>

          <Field label={t('billing.family.order')}>
            <Select
              value={form.family_tier_order}
              onValueChange={(v) => setForm({ ...form, family_tier_order: v as BillingSettingsUpdate['family_tier_order'] })}
            >
              <SelectTrigger className="w-64">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(['fee_desc', 'age_asc', 'age_desc'] as const).map((o) => (
                  <SelectItem key={o} value={o}>
                    {t(`billing.family.orders.${o}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field label={t('billing.family.appliesTo')}>
            <Select
              value={form.family_discount_invoice_type_id || 'all'}
              onValueChange={(v) =>
                setForm({ ...form, family_discount_invoice_type_id: v === 'all' ? '' : v })
              }
            >
              <SelectTrigger className="w-64">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('billing.family.appliesToAll')}</SelectItem>
                {(typesQuery.data ?? []).map((it) => (
                  <SelectItem key={it.id} value={it.id}>
                    {it.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>
      )}
    </fieldset>
  );
}

interface StripePanelProps {
  slug: string;
  token: string | null;
  account: StripeAccountStatus | undefined;
  isLoading: boolean;
  onChanged: () => void;
  confirm: ReturnType<typeof useConfirm>;
}

function StripePanel({ slug, token, account, isLoading, onChanged, confirm }: StripePanelProps) {
  const { t } = useTranslation('common');
  const auth = useAuth();
  const [country, setCountry] = useState<string>('SE');
  const [email, setEmail] = useState<string>(auth.user?.profile.email ?? '');

  const onboardingMutation = useMutation({
    mutationFn: async () => {
      const origin = window.location.origin;
      const base = `${origin}/orgs/${encodeURIComponent(slug)}/settings/billing`;
      const res = await startStripeOnboarding(token, slug, {
        country,
        email,
        refresh_url: `${base}?stripe=refresh`,
        return_url: `${base}?stripe=return`,
      });
      return res.url;
    },
    onSuccess: (url) => {
      window.location.href = url;
    },
  });

  const refreshMutation = useMutation({
    mutationFn: () => refreshStripeAccount(token, slug),
    onSuccess: () => {
      onChanged();
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: async () => {
      const ok = await confirm({
        title: t('billing.stripe.disconnectConfirmTitle'),
        body: t('billing.stripe.disconnectConfirmBody'),
        confirmLabel: t('billing.stripe.disconnect'),
        tone: 'destructive',
      });
      if (!ok) {
        throw new Error('cancelled');
      }
      return disconnectStripeAccount(token, slug);
    },
    onSuccess: () => {
      onChanged();
    },
  });

  if (isLoading) {
    return <FormSkeleton rows={5} />;
  }

  // Stripe-not-configured surfaces as a 503 from the API, which we don't
  // distinguish here; accountQuery is `connected: false` either way.
  if (!account || !account.connected) {
    return (
      <div className="flex flex-col gap-4 rounded-3xl border border-border/60 bg-card p-6">
        <header className="flex flex-col gap-1">
          <h2 className="flex items-center gap-2 text-lg font-semibold">
            <CreditCard weight="bold" className="h-5 w-5 shrink-0" />
            {t('billing.stripe.title')}
          </h2>
          <p className="text-sm text-muted-foreground">{t('billing.stripe.description')}</p>
        </header>
        <p className="text-sm text-muted-foreground">{t('billing.stripe.notConnected')}</p>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Field label={t('billing.stripe.country')}>
            <Select value={country} onValueChange={setCountry}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SUPPORTED_COUNTRIES.map((c) => (
                  <SelectItem key={c} value={c}>
                    {t(`billing.stripe.country_list.${c}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label={t('billing.stripe.email')}>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </Field>
        </div>

        <div>
          <Button
            type="button"
            disabled={onboardingMutation.isPending || !email}
            onClick={() => onboardingMutation.mutate()}
          >
            <Plug weight="bold" className="h-4 w-4 shrink-0" />
            {onboardingMutation.isPending
              ? t('billing.stripe.connecting')
              : t('billing.stripe.connectCta')}
          </Button>
        </div>
      </div>
    );
  }

  // Three-state status derived from the mirrored Stripe payload:
  //   incomplete — account row exists but `details_submitted=false` (the
  //                user bailed before submitting). `disabled_reason` in this
  //                state is always `requirements.past_due` and reads as
  //                "we shut you down" to a Swedish admin — suppress it.
  //   restricted — details were submitted but Stripe is still blocking
  //                charges or payouts. The requirements list is actionable.
  //   active     — everything green.
  const status: 'incomplete' | 'restricted' | 'active' = !account.details_submitted
    ? 'incomplete'
    : account.charges_enabled && account.payouts_enabled
      ? 'active'
      : 'restricted';

  // Resuming reuses the initial onboardingMutation — the backend's
  // StartOnboarding is idempotent: for an existing Connect account it ignores
  // country/email and just mints a fresh AccountLink to the same hosted flow.
  const resumeOnboarding = () => onboardingMutation.mutate();

  return (
    <div className="flex flex-col gap-4 rounded-3xl border border-border/60 bg-card p-6">
      <header className="flex items-center justify-between">
        <div className="flex flex-col gap-1">
          <h2 className="flex items-center gap-2 text-lg font-semibold">
            <PlugsConnected weight="bold" className="h-5 w-5 shrink-0" />
            {t('billing.stripe.title')}
          </h2>
          <p className="text-sm text-muted-foreground">
            {account.stripe_account_id} {' · '} {account.country}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            disabled={refreshMutation.isPending}
            onClick={() => refreshMutation.mutate()}
          >
            <ArrowClockwise weight="bold" className="h-4 w-4 shrink-0" />
            {refreshMutation.isPending ? t('billing.stripe.refreshing') : t('billing.stripe.refresh')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={disconnectMutation.isPending}
            onClick={() => disconnectMutation.mutate()}
          >
            {t('billing.stripe.disconnect')}
          </Button>
        </div>
      </header>

      <StatusSummary status={status} />

      {status === 'incomplete' && (
        <div className="flex flex-col gap-3">
          <div>
            <Button
              type="button"
              disabled={onboardingMutation.isPending}
              onClick={resumeOnboarding}
            >
              <Plug weight="bold" className="h-4 w-4 shrink-0" />
              {onboardingMutation.isPending
                ? t('billing.stripe.connecting')
                : t('billing.stripe.status.resumeOnboarding')}
            </Button>
          </div>
          {account.requirements_due.length > 0 && (
            <details className="group text-sm text-muted-foreground">
              <summary className="flex cursor-pointer select-none items-center gap-1 font-medium">
                <CaretRight
                  weight="bold"
                  className="h-3 w-3 shrink-0 transition-transform group-open:rotate-90"
                />
                {t('billing.stripe.status.showDetails')}
              </summary>
              <ul className="mt-2 list-inside list-disc pl-4">
                {dedupedStripeRequirements(account.requirements_due).map(({ key, raw }) => (
                  <li key={raw}>
                    {t(`billing.stripe.status.requirements.${key}`, { defaultValue: raw })}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      {status === 'restricted' && account.requirements_due.length > 0 && (
        <div className="rounded-2xl bg-amber-50 p-4 text-sm text-amber-900">
          <h3 className="mb-1 flex items-center gap-2 font-semibold">
            <Warning weight="bold" className="h-4 w-4 shrink-0" />
            {t('billing.stripe.status.requirementsDue')}
          </h3>
          <ul className="list-inside list-disc">
            {dedupedStripeRequirements(account.requirements_due).map(({ key, raw }) => (
              <li key={raw}>
                {t(`billing.stripe.status.requirements.${key}`, { defaultValue: raw })}
              </li>
            ))}
          </ul>
          <div className="pt-3">
            <Button
              type="button"
              disabled={onboardingMutation.isPending}
              onClick={resumeOnboarding}
            >
              {onboardingMutation.isPending
                ? t('billing.stripe.connecting')
                : t('billing.stripe.status.continueDetails')}
            </Button>
          </div>
        </div>
      )}

      {status === 'restricted' && account.disabled_reason && (
        <p className="text-sm text-amber-900">
          {t('billing.stripe.status.disabledReason', {
            reason: t(`billing.stripe.status.disabledReasons.${account.disabled_reason}`, {
              defaultValue: account.disabled_reason,
            }),
          })}
        </p>
      )}

      {account.last_synced_at && (
        <p className="text-xs text-muted-foreground">
          {t('billing.stripe.status.lastSynced', {
            when: new Date(account.last_synced_at).toLocaleString(),
          })}
        </p>
      )}
    </div>
  );
}

function StatusSummary({ status }: { status: 'incomplete' | 'restricted' | 'active' }) {
  const { t } = useTranslation('common');
  const palette =
    status === 'active'
      ? { dot: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-50' }
      : status === 'incomplete'
        ? { dot: 'bg-slate-400', text: 'text-slate-700', bg: 'bg-slate-50' }
        : { dot: 'bg-amber-500', text: 'text-amber-800', bg: 'bg-amber-50' };
  return (
    <div className={`flex items-center gap-3 rounded-2xl p-3 ${palette.bg}`}>
      <span className={`h-2.5 w-2.5 shrink-0 rounded-pill ${palette.dot}`} aria-hidden />
      <div className="flex flex-col">
        <span className={`text-sm font-semibold ${palette.text}`}>
          {t(`billing.stripe.status.summary.${status}`)}
        </span>
        <span className="text-xs text-muted-foreground">
          {t(`billing.stripe.status.summary.${status}Hint`)}
        </span>
      </div>
    </div>
  );
}
